/**
 * Pure equipment / inventory domain ops (M7-A).
 *
 * Collection is authoritative; equipped slots hold instanceId refs only.
 * No capacity limit (GDD: unlimited storage). No combat effects applied here.
 */

import {
  EQUIPMENT_SLOT_IDS,
  createEmptyEquippedSlots,
  filterMatchesCategory,
  getItemDef,
  isEquipmentSlotId,
  isKnownItemDef,
  type EquipmentSlotId,
  type ItemFilterId,
  type ItemRarity,
  type WeaponHandedness,
} from '../data/equipment';
import {
  canClassEquipItem,
  type ClassEquipRejectReason,
} from '../data/classEquipment';
import {
  meetsItemLevelClassRequirement,
  requiredClassLevelForItemLevel,
} from '../data/equipmentProgression';
import type { AffixRoll } from '../data/lootTables';
import { CLASS_LEVEL_CAP } from '../persistence/schema';

/** Unique owned instance. Defs stay in data/; instances carry identity + rarity. */
export interface ItemInstance {
  instanceId: string;
  defId: string;
  rarity: ItemRarity;
  acquiredAt: number;
  source?: string;
  /** Rolled affixes (M7-B). Persisted; combat application is M7-C. */
  affixes?: AffixRoll[];
  /** Item level band (M7-B). Persisted; combat scaling is M7-C. */
  itemLevel?: number;
}

export type EquippedSlots = Record<EquipmentSlotId, string | null>;

export interface InventoryState {
  /** Authoritative permanent collection (shared across classes). */
  items: ItemInstance[];
  /** Slot → instanceId. Missing / invalid refs are treated as empty. */
  equipped: EquippedSlots;
}

export type EquipRejectReason =
  | 'unknown_instance'
  | 'unknown_def'
  | 'slot_mismatch'
  | 'already_equipped_elsewhere'
  | 'two_hand_blocks_off_hand'
  | 'special_die_locked'
  | 'duplicate_instance_id'
  | 'class_level_insufficient'
  | ClassEquipRejectReason;

export interface EquipContext {
  /** From General talent — required to equip Special Die. */
  specialDieSlotUnlocked: boolean;
  /** Active class for permission checks (equip only). */
  classId?: string;
  /**
   * Active class level for Item Level equip requirements.
   * Defaults to 1 when omitted (strictest for high-ilvl items).
   */
  classLevel?: number;
}

export interface EquipResult {
  ok: true;
  inventory: InventoryState;
  /** Instance moved out of off-hand when equipping a two-hand weapon (still in bag). */
  unequippedToBag: string[];
}

export interface EquipFailure {
  ok: false;
  reason: EquipRejectReason;
}

export type EquipOutcome = EquipResult | EquipFailure;

export function createEmptyInventory(): InventoryState {
  return {
    items: [],
    equipped: createEmptyEquippedSlots(),
  };
}

export function newItemInstanceId(seed?: string | number): string {
  const t = Date.now().toString(36);
  const s = (seed ?? Math.floor(Math.random() * 1e9)).toString(36);
  return `item_${t}_${s}`;
}

export function createItemInstance(
  defId: string,
  options: {
    instanceId?: string;
    rarity?: ItemRarity;
    acquiredAt?: number;
    source?: string;
    affixes?: AffixRoll[];
    itemLevel?: number;
  } = {},
): ItemInstance {
  const def = getItemDef(defId);
  return {
    instanceId: options.instanceId ?? newItemInstanceId(defId),
    defId,
    rarity: options.rarity ?? def?.defaultRarity ?? 'common',
    acquiredAt: options.acquiredAt ?? Date.now(),
    source: options.source,
    affixes: options.affixes ? options.affixes.map((a) => ({ ...a })) : undefined,
    itemLevel: options.itemLevel,
  };
}

export function findItem(
  inventory: InventoryState,
  instanceId: string,
): ItemInstance | undefined {
  return inventory.items.find((i) => i.instanceId === instanceId);
}

export function isInstanceEquipped(
  inventory: InventoryState,
  instanceId: string,
): boolean {
  return EQUIPMENT_SLOT_IDS.some((slot) => inventory.equipped[slot] === instanceId);
}

export function slotOfInstance(
  inventory: InventoryState,
  instanceId: string,
): EquipmentSlotId | null {
  for (const slot of EQUIPMENT_SLOT_IDS) {
    if (inventory.equipped[slot] === instanceId) {
      return slot;
    }
  }
  return null;
}

export function getEquippedInstance(
  inventory: InventoryState,
  slot: EquipmentSlotId,
): ItemInstance | undefined {
  const id = inventory.equipped[slot];
  if (!id) return undefined;
  return findItem(inventory, id);
}

export function mainHandHandedness(
  inventory: InventoryState,
): WeaponHandedness | null {
  const main = getEquippedInstance(inventory, 'main_hand');
  if (!main) return null;
  return getItemDef(main.defId)?.handedness ?? null;
}

export function isOffHandDisabled(inventory: InventoryState): boolean {
  return mainHandHandedness(inventory) === 'two_hand';
}

/**
 * Add instances to the collection. Rejects duplicate instanceIds.
 * Does not auto-equip.
 */
export function addItemsToInventory(
  inventory: InventoryState,
  items: readonly ItemInstance[],
): { ok: true; inventory: InventoryState } | { ok: false; reason: 'duplicate_instance_id' } {
  if (items.length === 0) {
    return { ok: true, inventory };
  }
  const seen = new Set(inventory.items.map((i) => i.instanceId));
  for (const item of items) {
    if (seen.has(item.instanceId)) {
      return { ok: false, reason: 'duplicate_instance_id' };
    }
    seen.add(item.instanceId);
  }
  return {
    ok: true,
    inventory: {
      ...inventory,
      items: [...inventory.items, ...items],
    },
  };
}

function cloneEquipped(equipped: EquippedSlots): EquippedSlots {
  return { ...equipped };
}

function clearSlotRefsForMissingItems(inventory: InventoryState): InventoryState {
  const ids = new Set(inventory.items.map((i) => i.instanceId));
  const equipped = cloneEquipped(inventory.equipped);
  let changed = false;
  for (const slot of EQUIPMENT_SLOT_IDS) {
    const ref = equipped[slot];
    if (ref && !ids.has(ref)) {
      equipped[slot] = null;
      changed = true;
    }
  }
  return changed ? { ...inventory, equipped } : inventory;
}

/**
 * Drop unknown defs / broken equipped refs. Keeps talents/XP callers intact —
 * used by save migration normalize.
 */
export function sanitizeInventory(inventory: InventoryState): InventoryState {
  const items = inventory.items.filter(
    (item) =>
      typeof item.instanceId === 'string' &&
      item.instanceId.length > 0 &&
      typeof item.defId === 'string' &&
      item.defId.length > 0,
  );
  // Deduplicate by instanceId (first wins).
  const seen = new Set<string>();
  const unique: ItemInstance[] = [];
  for (const item of items) {
    if (seen.has(item.instanceId)) continue;
    seen.add(item.instanceId);
    unique.push(item);
  }
  const equipped = createEmptyEquippedSlots();
  for (const slot of EQUIPMENT_SLOT_IDS) {
    const ref = inventory.equipped[slot];
    if (ref && seen.has(ref)) {
      // One instance may only occupy one slot.
      const already = EQUIPMENT_SLOT_IDS.some(
        (s) => s !== slot && equipped[s] === ref,
      );
      if (!already) {
        equipped[slot] = ref;
      }
    }
  }
  return { items: unique, equipped };
}

export function validateEquip(
  inventory: InventoryState,
  instanceId: string,
  slot: EquipmentSlotId,
  ctx: EquipContext,
): EquipRejectReason | null {
  const item = findItem(inventory, instanceId);
  if (!item) return 'unknown_instance';
  if (!isKnownItemDef(item.defId)) return 'unknown_def';
  const def = getItemDef(item.defId)!;
  if (!def.allowedSlots.includes(slot)) return 'slot_mismatch';

  const currentSlot = slotOfInstance(inventory, instanceId);
  if (currentSlot && currentSlot !== slot) {
    // Re-equip to a different valid slot is allowed via unequip+equip or swap path.
    // Direct equip while elsewhere is rejected to keep ops explicit.
    return 'already_equipped_elsewhere';
  }

  if (slot === 'special_die' && !ctx.specialDieSlotUnlocked) {
    return 'special_die_locked';
  }

  if (slot === 'off_hand') {
    // Existing 2H main blocks off-hand (unless we are replacing main elsewhere).
    if (isOffHandDisabled(inventory)) {
      return 'two_hand_blocks_off_hand';
    }
  }

  if (ctx.classId) {
    const classCheck = canClassEquipItem(ctx.classId, def);
    if (!classCheck.ok) {
      return classCheck.reason;
    }
  }

  const classLevel = Math.max(1, Math.floor(ctx.classLevel ?? 1));
  if (!meetsItemLevelClassRequirement(classLevel, item.itemLevel)) {
    return 'class_level_insufficient';
  }

  return null;
}

/** Human-readable equip rejection for UI. */
export function describeEquipReject(
  reason: EquipRejectReason,
  detail?: { requiredClassLevel?: number; itemLevel?: number },
): string {
  switch (reason) {
    case 'two_hand_blocks_off_hand':
      return 'Two-hand weapon blocks off-hand';
    case 'special_die_locked':
      return 'Special Die locked — unlock via General talent';
    case 'slot_mismatch':
      return 'Item does not fit that slot';
    case 'class_weapon_forbidden':
      return 'Your class cannot equip this weapon';
    case 'class_shield_forbidden':
      return 'Your class cannot equip shields';
    case 'class_focus_forbidden':
      return 'Your class cannot equip magical foci';
    case 'unknown_class_permissions':
      return 'Class equipment permissions unavailable';
    case 'class_level_insufficient': {
      const req =
        detail?.requiredClassLevel ??
        requiredClassLevelForItemLevel(detail?.itemLevel);
      return `Requires class level ${req}`;
    }
    case 'unknown_instance':
      return 'Item not in inventory';
    case 'unknown_def':
      return 'Unknown item definition';
    default:
      return `Cannot equip (${reason})`;
  }
}

/**
 * Equip an unequipped bag item into a slot.
 * Two-hand main-hand: safely unequips off-hand into the bag (never deletes).
 * Replacing an occupied slot returns the previous item to the bag (still in collection).
 */
export function equipItem(
  inventory: InventoryState,
  instanceId: string,
  slot: EquipmentSlotId,
  ctx: EquipContext,
): EquipOutcome {
  const inv = clearSlotRefsForMissingItems(inventory);
  const reason = validateEquip(inv, instanceId, slot, ctx);
  if (reason === 'already_equipped_elsewhere') {
    // Allow move: unequip first then continue.
    const from = slotOfInstance(inv, instanceId);
    if (!from) return { ok: false, reason };
    const afterUnequip = unequipItem(inv, from);
    return equipItem(afterUnequip, instanceId, slot, ctx);
  }
  if (reason) {
    return { ok: false, reason };
  }

  const item = findItem(inv, instanceId)!;
  const def = getItemDef(item.defId)!;
  const equipped = cloneEquipped(inv.equipped);
  const unequippedToBag: string[] = [];

  // Occupant of target slot stays in collection; only clear the ref.
  equipped[slot] = instanceId;

  if (slot === 'main_hand' && def.handedness === 'two_hand' && equipped.off_hand) {
    unequippedToBag.push(equipped.off_hand);
    equipped.off_hand = null;
  }

  return {
    ok: true,
    inventory: { ...inv, equipped },
    unequippedToBag,
  };
}

/** Clear a slot ref. Item remains in the collection. */
export function unequipItem(
  inventory: InventoryState,
  slot: EquipmentSlotId,
): InventoryState {
  if (!inventory.equipped[slot]) {
    return inventory;
  }
  const equipped = cloneEquipped(inventory.equipped);
  equipped[slot] = null;
  return { ...inventory, equipped };
}

/**
 * Compatibility policy (M7-D2): when class level is too low for an equipped
 * item's Item Level requirement, unequip into the bag. Never delete or reroll.
 */
export function unequipItemsFailingClassLevelRequirement(
  inventory: InventoryState,
  classLevel: number,
): { inventory: InventoryState; unequippedInstanceIds: string[] } {
  const cl = Math.max(1, Math.min(CLASS_LEVEL_CAP, Math.floor(classLevel)));
  let next = inventory;
  const unequippedInstanceIds: string[] = [];
  for (const slot of EQUIPMENT_SLOT_IDS) {
    const item = getEquippedInstance(next, slot);
    if (!item) continue;
    if (meetsItemLevelClassRequirement(cl, item.itemLevel)) continue;
    unequippedInstanceIds.push(item.instanceId);
    next = unequipItem(next, slot);
  }
  return { inventory: next, unequippedInstanceIds };
}

/**
 * Swap two slots' refs (both may be empty). Does not move items out of the bag
 * collection. Validates special-die lock and 2H/off-hand constraints after swap.
 */
export function swapEquippedSlots(
  inventory: InventoryState,
  slotA: EquipmentSlotId,
  slotB: EquipmentSlotId,
  ctx: EquipContext,
): EquipOutcome {
  if (slotA === slotB) {
    return { ok: true, inventory, unequippedToBag: [] };
  }
  const equipped = cloneEquipped(inventory.equipped);
  const a = equipped[slotA];
  const b = equipped[slotB];
  equipped[slotA] = b;
  equipped[slotB] = a;

  let next: InventoryState = { ...inventory, equipped };

  // Validate each occupied slot against its new occupant.
  for (const slot of [slotA, slotB] as const) {
    const id = next.equipped[slot];
    if (!id) continue;
    // Temporarily clear so validateEquip doesn't see "already_equipped_elsewhere".
    const probeEquipped = cloneEquipped(next.equipped);
    for (const s of EQUIPMENT_SLOT_IDS) {
      if (probeEquipped[s] === id) probeEquipped[s] = null;
    }
    probeEquipped[slot] = null;
    const probe = { ...next, equipped: probeEquipped };
    const reason = validateEquip(probe, id, slot, ctx);
    if (reason) {
      return { ok: false, reason };
    }
  }

  // 2H main still forces off-hand empty.
  const main = getEquippedInstance(next, 'main_hand');
  if (main && getItemDef(main.defId)?.handedness === 'two_hand' && next.equipped.off_hand) {
    const moved = next.equipped.off_hand;
    next = {
      ...next,
      equipped: { ...next.equipped, off_hand: null },
    };
    return { ok: true, inventory: next, unequippedToBag: [moved] };
  }

  return { ok: true, inventory: next, unequippedToBag: [] };
}

/** Unequipped (bag) items, optionally filtered. */
export function listBagItems(
  inventory: InventoryState,
  filter: ItemFilterId = 'all',
): ItemInstance[] {
  return inventory.items.filter((item) => {
    if (isInstanceEquipped(inventory, item.instanceId)) return false;
    const def = getItemDef(item.defId);
    if (!def) {
      return filter === 'all';
    }
    return filterMatchesCategory(def.category, filter);
  });
}

/** All items matching filter (equipped + bag). */
export function listItemsByFilter(
  inventory: InventoryState,
  filter: ItemFilterId = 'all',
): ItemInstance[] {
  return inventory.items.filter((item) => {
    const def = getItemDef(item.defId);
    if (!def) return filter === 'all';
    return filterMatchesCategory(def.category, filter);
  });
}

export function displayNameForItem(item: ItemInstance): string {
  return getItemDef(item.defId)?.name ?? `Unknown (${item.defId})`;
}

export function inventoryFromPermanentFields(fields: {
  items: ItemInstance[];
  equipped: EquippedSlots;
}): InventoryState {
  return sanitizeInventory({
    items: fields.items,
    equipped: fields.equipped,
  });
}

export { isEquipmentSlotId, EQUIPMENT_SLOT_IDS };
